import { useEffect, useState } from "react";
import request from "project-config/services/request/request";
import { IExportData } from "@/pages/export-list/types";
import useForm from "antd/es/form/hooks/useForm";
import { useGlobalStore } from "@/store/GlobalStore";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { FieldMap, AppealStatus } from "../constant";
import { getTranslateNsT } from "@/utils/getTranslateNsT";

export interface BillAppealProgressProps {
  [key: string]: number | string;
  appealStatus: number;
  time: string;
  appealResult: string;
  statisLabel: string;
}

export function useAppealBill() {
  const { isForeign } = useGlobalStore();

  const [billInfo, setBillInfo] = useState<IExportData>();
  const [isShowProgressForm, setIsShowProgressForm] = useState(false);
  const [appealProgressList, setAppealProgressList] =
    useState<BillAppealProgressProps[]>();
  const [revokeId, setRevokeId] = useState(0);
  const [currentAppealStatus, setCurrentAppealStatus] = useState("");
  const [openRevokeModal, setRevokeModal] = useState<number>();

  const [concatInfo, setConcatInfo] = useState<{
    userName: string;
    userId: number;
    email?: string;
    phone?: string;
  }>();

  useEffect(() => {
    if (!billInfo) return;

    async function getUserConcat() {
      const { data } = await request.post(
        "/api/rendering/user/consume/bill/getUserContactInfo",
        {
          userId: billInfo?.userId,
        }
      );

      setConcatInfo(data);
    }
    setIsShowProgressForm(typeof billInfo?.appealStatus === "number");
    getUserConcat().then();
  }, [billInfo]);

  const { t } = useTranslation("center");

  const [appealForm] = useForm();
  useEffect(() => {
    appealForm.setFieldsValue({
      billNo: billInfo?.billNo,
      billCycle: billInfo?.consumeDate,
      userName: concatInfo?.userName,
      userId: concatInfo?.userId,
      appealBillNo: billInfo?.appealBillNo,
      connectInformation: isForeign
        ? concatInfo?.email || concatInfo?.phone
        : concatInfo?.phone || concatInfo?.email,
    });
  }, [appealForm, billInfo, concatInfo, isForeign]);

  const onOk = () => {
    return appealForm.validateFields().then(async (v) => {
      const { code } = await request.post(
        "/api/rendering/user/consume/bill/submitAppealBills",
        { ...v }
      );

      if (code === 200) {
        message.success(t("consumption_bill_appeal_success"));
        return;
      }

      message.error(t("consumption_bill_appeal_fail"));
    });
  };

  let billAppealProgress: BillAppealProgressProps[] = [];
  let pendingProgress: BillAppealProgressProps[] = [];

  const createBillAppealProgress = (
    progress: BillAppealProgressProps,
    billAppealProgress: BillAppealProgressProps[] = [],
    baseStatus = AppealStatus.Submitted
  ) => {
    // @ts-ignore
    const mateInfo = FieldMap[baseStatus];
    const { appealStatus, appealResult } = progress;
    const finished = [
      AppealStatus.Finished,
      AppealStatus.Reject,
      AppealStatus.Withdraw,
    ].includes(baseStatus);
    if (mateInfo && progress[mateInfo.time]) {
      billAppealProgress.push({
        appealResult: finished ? appealResult : "",
        appealStatus: baseStatus,
        statisLabel: t(`consumption_bill_appeal_status_${baseStatus + 1}`),
        // @ts-ignore
        time: progress[mateInfo.time],
      });
    }
    if ((mateInfo && !mateInfo.next) || baseStatus === appealStatus) {
      if (finished) return;
      const t = getTranslateNsT("center");
      const staticLabel =
        appealStatus === AppealStatus.Submitted
          ? t("consumption_bill_appeal_status_2")
          : t("consumption_bill_appeal_result");
      pendingProgress.push({
        statisLabel: staticLabel,
        appealStatus: -1,
        appealResult: "",
        // @ts-ignore
        time: progress[mateInfo.time] ? progress[mateInfo.time] : "",
      });
      if (appealStatus === AppealStatus.Submitted) {
        pendingProgress.push({
          statisLabel: t("consumption_bill_appeal_result"),
          appealStatus: -1,
          appealResult: "",
          time: "",
        });
      }
      return;
    }
    if (!mateInfo) return;
    const nextStatus =
      Array.isArray(mateInfo.next) && mateInfo.next.includes(appealStatus)
        ? appealStatus
        : mateInfo.next || [];
    // @ts-ignore
    createBillAppealProgress(progress, billAppealProgress, nextStatus);
  };

  const showComplaintProgressDialog = async (
    row?: IExportData,
    isShowRevokeModal?: boolean
  ) => {
    if (!row) return;
    const { billNo, appealBillNo, consumeDate: billCycle } = row;
    const { code, data } = await request.post(
      "/api/rendering/user/consume/bill/billAppealProgress",
      {
        billNo,
        appealBillNo,
        billCycle,
      }
    );
    if (code !== 200) return;
    setRevokeId(data.id);
    if (isShowRevokeModal) {
      setRevokeModal(data.id);
    }
    setCurrentAppealStatus(data.appealStatus);
    billAppealProgress = [];
    pendingProgress = [];
    await createBillAppealProgress(data, billAppealProgress);
    setAppealProgressList(billAppealProgress);
    if (!pendingProgress.length) return;
    setAppealProgressList([...billAppealProgress, ...pendingProgress]);
  };

  const fetchAppealData = async (params?: string) => {
    const { data } = await request.post(
      "/api/rendering/user/consume/bill/getUserMonthBills",
      {
        billNo: params,
        pageNum: 1,
        pageSize: 10,
      }
    );
    if (!data.items.length) return;
    return data.items[0];
  };

  return {
    concatInfo,
    billInfo,
    setBillInfo,
    appealForm,
    onOk,
    revokeId,
    isShowProgressForm,
    setIsShowProgressForm,
    showComplaintProgressDialog,
    appealProgressList,
    setAppealProgressList,
    fetchAppealData,
    currentAppealStatus,
    openRevokeModal,
    setRevokeModal,
  };
}
